import React, { useEffect, useState, useCallback } from 'react';
import { Activity, RefreshCw, Clock, AlertCircle } from 'lucide-react';

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';
const POLL_INTERVAL_MS = 30_000;

interface WorkflowItem {
  epic_id: string;
  epic_title: string;
  task_id: string;
  current_place: string;
  last_transition: string;
  status: string;
  wsjf: number;
  quality_tier: string;
  quality_colour: string;
  time_in_state_seconds: number | null;
}

const TIER_CLASSES: Record<string, string> = {
  gray: 'bg-slate-700 text-slate-300',
  green: 'bg-emerald-900 text-emerald-200',
  blue: 'bg-blue-900 text-blue-200',
  purple: 'bg-purple-900 text-purple-200',
};

const STATUS_CLASSES: Record<string, string> = {
  in_progress: 'text-cyan-400',
  failed: 'text-red-400',
  blocked: 'text-yellow-400',
};

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function TierBadge({ tier, colour }: { tier: string; colour: string }) {
  const cls = TIER_CLASSES[colour] ?? TIER_CLASSES.gray;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {tier}
    </span>
  );
}

function WorkflowRow({ item }: { item: WorkflowItem }) {
  const statusCls = STATUS_CLASSES[item.status] ?? 'text-slate-400';
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-100 truncate flex-1">
          {item.epic_title}
        </span>
        <TierBadge tier={item.quality_tier} colour={item.quality_colour} />
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
        <span className="font-mono text-slate-500">{item.epic_id}</span>
        <span className={`font-medium ${statusCls}`}>{item.current_place}</span>
        <span className="text-slate-500">→</span>
        <span className="text-slate-300">{item.last_transition}</span>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500 mt-0.5">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDuration(item.time_in_state_seconds)}
        </span>
        <span>WSJF {item.wsjf.toFixed(0)}</span>
        <span className={statusCls}>{item.status.replace('_', ' ')}</span>
      </div>
    </div>
  );
}

export const WorkflowMonitor: React.FC = () => {
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchWorkflows = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/workflow-monitor`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: WorkflowItem[] = await res.json();
      setWorkflows(data);
      setLastRefresh(new Date());
      setError('');
    } catch (err: any) {
      setError(err.message ?? 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
    const interval = setInterval(fetchWorkflows, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchWorkflows]);

  const inProgress = workflows.filter(w => w.status === 'in_progress');
  const failed = workflows.filter(w => w.status === 'failed');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-cyan-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Workflow Monitor</h1>
            <p className="text-slate-400 text-sm">Live Petri net state from petri_net_state.db</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-slate-500">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchWorkflows}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-900/30 border border-red-700 rounded-lg px-4 py-3 text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex gap-4 text-sm">
        <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 flex-1 text-center">
          <div className="text-2xl font-bold text-cyan-400">{inProgress.length}</div>
          <div className="text-slate-400 text-xs mt-1">In Progress</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 flex-1 text-center">
          <div className="text-2xl font-bold text-red-400">{failed.length}</div>
          <div className="text-slate-400 text-xs mt-1">Failed</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 flex-1 text-center">
          <div className="text-2xl font-bold text-slate-100">{workflows.length}</div>
          <div className="text-slate-400 text-xs mt-1">Total Active</div>
        </div>
      </div>

      <div className="mb-2 text-xs text-slate-500 flex gap-4">
        <span><span className="inline-block w-2 h-2 rounded-full bg-slate-600 mr-1" />gray = unverified</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-600 mr-1" />green = verified</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-blue-600 mr-1" />blue = high-quality</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-purple-600 mr-1" />purple = exceptional</span>
      </div>

      {loading && workflows.length === 0 ? (
        <div className="text-slate-400 text-center py-12">Loading workflows…</div>
      ) : workflows.length === 0 ? (
        <div className="text-slate-500 text-center py-12">No active workflows found.</div>
      ) : (
        <div className="space-y-3">
          {inProgress.length > 0 && (
            <>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                In Progress ({inProgress.length})
              </h2>
              {inProgress.map(w => (
                <WorkflowRow key={`${w.epic_id}-${w.task_id}`} item={w} />
              ))}
            </>
          )}
          {failed.length > 0 && (
            <>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mt-4">
                Failed ({failed.length})
              </h2>
              {failed.map(w => (
                <WorkflowRow key={`${w.epic_id}-${w.task_id}`} item={w} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
